import React, { useEffect, useMemo, useState } from 'react'

import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    ButtonBase,
    Typography,
} from '@material-ui/core'
import ScheduleIcon from '@material-ui/icons/Schedule'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import { useTurnReviewDialogStyles } from '../styles'
import {
    getServiceVendorWorkorderCountsForPhase,
    ServiceVendorWorkorderGroup,
} from '../services/turnReviewStats'
import { getServiceImageDetails } from '../../../../../models'
import {
    ezScheduleDetails,
    SchedulePhase,
    TemplateWorkorder,
    UnitCoverage,
} from '../../../../../models/WorkOrder/types'
import {
    TemplateWorkordersByPhaseEntry,
    UnitCoverageByPhaseEntry,
} from '../../../../../hooks/useEzSchedule/types'

interface Props {
    dialogOpen: boolean
    handleDialogClose: () => void
    onSchedulePlannedWorkorders: (phaseIds: number[]) => void
    templateWorkorders?: TemplateWorkorder[]
    ezSchedule?: ezScheduleDetails
    unitCoverage?: UnitCoverage
    selectedPhaseId?: number
    schedulePhases: SchedulePhase[]
    templateWorkordersByPhase: TemplateWorkordersByPhaseEntry[]
    unitCoverageByPhase: UnitCoverageByPhaseEntry[]
}

type PhaseSelection = number | 'all'

export const TurnReviewDialog = (props: Props) => {
    const {
        dialogOpen,
        handleDialogClose,
        onSchedulePlannedWorkorders,
        ezSchedule,
        unitCoverage,
        selectedPhaseId,
        schedulePhases,
        templateWorkordersByPhase,
        unitCoverageByPhase,
    } = props
    const classes = useTurnReviewDialogStyles()
    const defaultSelectedPhaseId = selectedPhaseId ?? schedulePhases[0]?.id
    const [selectedPhase, setSelectedPhase] = useState<PhaseSelection>(
        defaultSelectedPhaseId ?? 'all',
    )
    const [lastSelectedPhaseId, setLastSelectedPhaseId] = useState<
        number | undefined
    >(defaultSelectedPhaseId)

    const isMultiPhase = schedulePhases.length > 1
    const isAllPhasesView = selectedPhase === 'all'
    const selectedPhaseIdForDetails =
        selectedPhase === 'all'
            ? lastSelectedPhaseId ?? selectedPhaseId ?? schedulePhases[0]?.id
            : selectedPhase

    const handleSelectPhase = (phaseId: number) => {
        setSelectedPhase(phaseId)
        setLastSelectedPhaseId(phaseId)
    }

    const handleSelectAllPhases = () => {
        setSelectedPhase('all')
    }

    useEffect(() => {
        if (!dialogOpen) {
            return
        }

        const nextSelectedPhaseId = selectedPhaseId ?? schedulePhases[0]?.id
        if (nextSelectedPhaseId === undefined) {
            setSelectedPhase('all')
            setLastSelectedPhaseId(undefined)
            return
        }

        setSelectedPhase(nextSelectedPhaseId)
        setLastSelectedPhaseId(nextSelectedPhaseId)
    }, [dialogOpen, selectedPhaseId, schedulePhases])

    const phaseReviewData = useMemo(
        () =>
            schedulePhases.map((schedulePhase) => {
                const phaseData = ezSchedule?.phases?.find(
                    (p) => p.phase.id === schedulePhase.id,
                )
                return {
                    phase: schedulePhase,
                    phaseData,
                    templateWorkorders:
                        templateWorkordersByPhase.find(
                            (e) => e.phase.id === schedulePhase.id,
                        )?.template_workorders ?? [],
                    unitCoverage: unitCoverageByPhase.find(
                        (e) => e.phase.id === schedulePhase.id,
                    )?.unit_coverage,
                }
            }),
        [
            schedulePhases,
            templateWorkordersByPhase,
            unitCoverageByPhase,
            ezSchedule?.phases,
        ],
    )

    const selectedPhaseReviewData = useMemo(() => {
        if (selectedPhaseIdForDetails === undefined) {
            return phaseReviewData[0]
        }
        return phaseReviewData.find(
            (d) => d.phase.id === selectedPhaseIdForDetails,
        )
    }, [phaseReviewData, selectedPhaseIdForDetails])

    const selectedUnitCoverage =
        selectedPhaseReviewData?.unitCoverage ?? unitCoverage

    const unitCoverageStats = useMemo(() => {
        const coverageStatsSource = isAllPhasesView
            ? phaseReviewData.reduce(
                  (agg, d) => {
                      const c = d.unitCoverage
                      return {
                          unitsInProject:
                              agg.unitsInProject + (c?.units_in_project ?? 0),
                          unitsRequiringService:
                              agg.unitsRequiringService +
                              (c?.units_requiring_service ?? 0),
                          unitsScheduled:
                              agg.unitsScheduled + (c?.units_scheduled ?? 0),
                          doubleTransferUnits:
                              agg.doubleTransferUnits +
                              (c?.double_transfer_units ?? 0),
                          priorityUnitsReady:
                              agg.priorityUnitsReady +
                              (c?.priority_units_ready ?? 0),
                          priorityUnitsNotReady:
                              agg.priorityUnitsNotReady +
                              (c?.priority_units_not_ready ?? 0),
                          deferredUnitsDelayed:
                              agg.deferredUnitsDelayed +
                              (c?.deferred_units_delayed ?? 0),
                          deferredUnitsNotDelayed:
                              agg.deferredUnitsNotDelayed +
                              (c?.deferred_units_not_delayed ?? 0),
                      }
                  },
                  {
                      unitsInProject: 0,
                      unitsRequiringService: 0,
                      unitsScheduled: 0,
                      doubleTransferUnits: 0,
                      priorityUnitsReady: 0,
                      priorityUnitsNotReady: 0,
                      deferredUnitsDelayed: 0,
                      deferredUnitsNotDelayed: 0,
                  },
              )
            : {
                  unitsInProject: selectedUnitCoverage?.units_in_project ?? 0,
                  unitsRequiringService:
                      selectedUnitCoverage?.units_requiring_service ?? 0,
                  unitsScheduled: selectedUnitCoverage?.units_scheduled ?? 0,
                  doubleTransferUnits:
                      selectedUnitCoverage?.double_transfer_units ?? 0,
                  priorityUnitsReady:
                      selectedUnitCoverage?.priority_units_ready ?? 0,
                  priorityUnitsNotReady:
                      selectedUnitCoverage?.priority_units_not_ready ?? 0,
                  deferredUnitsDelayed:
                      selectedUnitCoverage?.deferred_units_delayed ?? 0,
                  deferredUnitsNotDelayed:
                      selectedUnitCoverage?.deferred_units_not_delayed ?? 0,
              }

        const priorityDenom =
            coverageStatsSource.priorityUnitsReady +
            coverageStatsSource.priorityUnitsNotReady
        const deferredDenom =
            coverageStatsSource.deferredUnitsDelayed +
            coverageStatsSource.deferredUnitsNotDelayed
        const pct = (num: number, den: number) =>
            den <= 0 ? '0.0%' : `${((num / den) * 100).toFixed(1)}%`

        const hasTransferDates = isAllPhasesView
            ? phaseReviewData.some(
                  (d) =>
                      d.phaseData?.transfer_start_date != null ||
                      d.phaseData?.transfer_end_date != null,
              )
            : selectedPhaseReviewData?.phaseData?.transfer_start_date != null ||
              selectedPhaseReviewData?.phaseData?.transfer_end_date != null

        return {
            unitsInProject: coverageStatsSource.unitsInProject,
            unitsRequiringService: coverageStatsSource.unitsRequiringService,
            unitsScheduled: coverageStatsSource.unitsScheduled,
            doubleTransferUnits: coverageStatsSource.doubleTransferUnits,
            hasTransferDates,
            priority: {
                numerator: coverageStatsSource.priorityUnitsReady,
                denominator: priorityDenom,
                percent: pct(
                    coverageStatsSource.priorityUnitsReady,
                    priorityDenom,
                ),
            },
            deferred: {
                numerator: coverageStatsSource.deferredUnitsDelayed,
                denominator: deferredDenom,
                percent: pct(
                    coverageStatsSource.deferredUnitsDelayed,
                    deferredDenom,
                ),
            },
        }
    }, [
        isAllPhasesView,
        phaseReviewData,
        selectedUnitCoverage,
        selectedPhaseReviewData,
    ])

    const serviceVendorCountsByPhase = useMemo(
        () =>
            phaseReviewData.map((d) => ({
                phase: d.phase,
                groups: getServiceVendorWorkorderCountsForPhase(
                    d.templateWorkorders,
                    d.phaseData,
                ),
            })),
        [phaseReviewData],
    )

    const selectedServiceVendorCounts: ServiceVendorWorkorderGroup[] = isAllPhasesView
        ? serviceVendorCountsByPhase.flatMap((p) => p.groups)
        : getServiceVendorWorkorderCountsForPhase(
              selectedPhaseReviewData?.templateWorkorders,
              selectedPhaseReviewData?.phaseData,
          )

    const formatCurrency = (value: number): string =>
        value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })

    const renderServiceVendorList = (
        groups: ServiceVendorWorkorderGroup[],
        keyPrefix: string,
    ) => {
        if (groups.length === 0) {
            return (
                <Typography color="textSecondary">
                    No template services for this phase.
                </Typography>
            )
        }
        return (
            <Box className={classes.serviceCardsGrid}>
                {groups.map((group) => {
                    const serviceTotal = group.vendors.reduce(
                        (sum, v) => sum + v.workorderCount,
                        0,
                    )
                    const serviceDetails = getServiceImageDetails(
                        group.serviceId ?? -1,
                    )
                    const iconSrc = serviceDetails.icon
                    const color =
                        group.serviceColor ?? serviceDetails.color ?? '#757575'
                    return (
                        <Box
                            key={`${keyPrefix}-${group.serviceName}-${group.servicePosition}`}
                            className={classes.serviceCard}
                            style={{
                                borderLeftWidth: 4,
                                borderLeftStyle: 'solid',
                                borderLeftColor: color,
                            }}
                        >
                            <Box className={classes.serviceCardHeader}>
                                {iconSrc && (
                                    <img
                                        src={iconSrc}
                                        alt=""
                                        className={classes.serviceCardIcon}
                                        style={{
                                            filter: `brightness(0.9)`,
                                        }}
                                    />
                                )}
                                <Typography
                                    className={classes.serviceCardName}
                                    style={{ color }}
                                >
                                    {group.serviceName}
                                </Typography>
                                <Typography
                                    className={classes.serviceCardTotal}
                                >
                                    {serviceTotal.toLocaleString()}
                                </Typography>
                            </Box>
                            <Box className={classes.serviceCardVendorTable}>
                                <Box className={classes.serviceCardVendorRow}>
                                    <Typography
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: 'rgba(0,0,0,0.4)',
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                        }}
                                    >
                                        Vendor
                                    </Typography>
                                    <Typography
                                        className={
                                            classes.serviceCardVendorStat
                                        }
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: 'rgba(0,0,0,0.4)',
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            marginRight: 16,
                                        }}
                                    >
                                        Units
                                    </Typography>
                                    <Typography
                                        className={
                                            classes.serviceCardVendorStat
                                        }
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: 'rgba(0,0,0,0.4)',
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            marginRight: 16,
                                        }}
                                    >
                                        Avg WO/Day
                                    </Typography>
                                    <Typography
                                        className={
                                            classes.serviceCardVendorStat
                                        }
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: 'rgba(0,0,0,0.4)',
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                        }}
                                    >
                                        Cost
                                    </Typography>
                                </Box>
                                {group.vendors.map((v) => (
                                    <Box
                                        key={`${keyPrefix}-${group.serviceName}-${v.templateServiceVendorId}`}
                                        className={classes.serviceCardVendorRow}
                                    >
                                        <Typography
                                            className={
                                                classes.serviceCardVendorName
                                            }
                                        >
                                            {v.vendorName}
                                        </Typography>
                                        <Typography
                                            className={
                                                classes.serviceCardVendorStat
                                            }
                                            style={{ marginRight: 24 }}
                                        >
                                            <strong>{v.totalUnits}</strong>
                                        </Typography>
                                        <Typography
                                            className={
                                                classes.serviceCardVendorStat
                                            }
                                            style={{ marginRight: 24 }}
                                        >
                                            <strong>
                                                {v.avgWoPerDay.toFixed(1)}
                                            </strong>
                                        </Typography>
                                        <Typography
                                            className={
                                                classes.serviceCardVendorStat
                                            }
                                        >
                                            <strong>
                                                {formatCurrency(
                                                    v.forecastedCost,
                                                )}
                                            </strong>
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )
                })}
            </Box>
        )
    }

    return (
        <Dialog
            open={dialogOpen}
            onClose={handleDialogClose}
            maxWidth={false}
            PaperProps={{ className: classes.paper }}
        >
            <DialogTitle>
                <Box className={classes.reviewTitleRow}>
                    <Typography variant="h6">Review Plan</Typography>
                    {isMultiPhase && (
                        <Box className={classes.reviewPhasePillRow}>
                            {schedulePhases.map((phase) => (
                                <ButtonBase
                                    key={phase.id}
                                    className={`${classes.reviewPhasePill} ${
                                        selectedPhase === phase.id
                                            ? classes.reviewPhasePillActive
                                            : ''
                                    }`}
                                    onClick={() => handleSelectPhase(phase.id)}
                                >
                                    {`Phase ${phase.phase_index}`}
                                </ButtonBase>
                            ))}
                            <ButtonBase
                                className={`${classes.reviewPhasePill} ${
                                    isAllPhasesView
                                        ? classes.reviewPhasePillActive
                                        : ''
                                }`}
                                onClick={handleSelectAllPhases}
                            >
                                All Phases
                            </ButtonBase>
                        </Box>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent className={classes.content}>
                <Box
                    className={classes.statsRow}
                    style={{
                        gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
                    }}
                >
                    <Box className={classes.statTile}>
                        <Typography className={classes.statLabel}>
                            {isAllPhasesView || !isMultiPhase
                                ? 'Units in Project'
                                : 'Units in Phase'}
                        </Typography>
                        <Typography className={classes.statValue}>
                            {unitCoverageStats.unitsInProject.toLocaleString()}
                        </Typography>
                    </Box>
                    <Box className={classes.statTile}>
                        <Typography className={classes.statLabel}>
                            Units Requiring Service
                        </Typography>
                        <Typography className={classes.statValue}>
                            {unitCoverageStats.unitsRequiringService.toLocaleString()}
                        </Typography>
                    </Box>
                    <Box className={classes.statTile}>
                        <Typography className={classes.statLabel}>
                            Units Scheduled
                        </Typography>
                        <Typography className={classes.statValue}>
                            {unitCoverageStats.unitsScheduled.toLocaleString()}
                        </Typography>
                    </Box>
                    <Box className={classes.statTile}>
                        <Typography className={classes.statLabel}>
                            Double Transfer Units
                        </Typography>
                        <Typography className={classes.statValue}>
                            {unitCoverageStats.doubleTransferUnits.toLocaleString()}
                        </Typography>
                    </Box>
                    <Box className={classes.statTile}>
                        <Typography className={classes.statLabel}>
                            Priority Units Ready
                        </Typography>
                        <Typography className={classes.statValue}>
                            {unitCoverageStats.hasTransferDates
                                ? `${unitCoverageStats.priority.numerator}/${unitCoverageStats.priority.denominator}`
                                : 'N/A'}
                        </Typography>
                        <Typography className={classes.statSecondaryValue}>
                            {unitCoverageStats.hasTransferDates
                                ? unitCoverageStats.priority.percent
                                : '-'}
                        </Typography>
                    </Box>
                    <Box className={classes.statTile}>
                        <Typography className={classes.statLabel}>
                            Deferred Units Delayed
                        </Typography>
                        <Typography className={classes.statValue}>
                            {unitCoverageStats.hasTransferDates
                                ? `${unitCoverageStats.deferred.numerator}/${unitCoverageStats.deferred.denominator}`
                                : 'N/A'}
                        </Typography>
                        <Typography className={classes.statSecondaryValue}>
                            {unitCoverageStats.hasTransferDates
                                ? unitCoverageStats.deferred.percent
                                : '-'}
                        </Typography>
                    </Box>
                </Box>

                <Typography
                    className={classes.servicesSectionTitle}
                    style={{ marginTop: 8 }}
                >
                    Services &amp; Vendors
                </Typography>
                {isAllPhasesView ? (
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24,
                        }}
                    >
                        {serviceVendorCountsByPhase.map(({ phase, groups }) => (
                            <Box key={phase.id}>
                                <Typography
                                    className={classes.sectionTitle}
                                    style={{ marginBottom: 8 }}
                                >
                                    Phase {phase.phase_index}
                                </Typography>
                                {renderServiceVendorList(
                                    groups,
                                    `phase-${phase.id}`,
                                )}
                            </Box>
                        ))}
                    </Box>
                ) : (
                    renderServiceVendorList(
                        selectedServiceVendorCounts,
                        'selected',
                    )
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<ExitToAppIcon />}
                    onClick={handleDialogClose}
                >
                    Exit &amp; Make Changes
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    startIcon={<ScheduleIcon />}
                    onClick={() => {
                        handleDialogClose()
                        const allPhaseIds = schedulePhases.map((p) => p.id)
                        if (isMultiPhase && isAllPhasesView) {
                            onSchedulePlannedWorkorders(allPhaseIds)
                            return
                        }
                        if (selectedPhase !== 'all') {
                            onSchedulePlannedWorkorders([selectedPhase])
                            return
                        }
                        if (lastSelectedPhaseId !== undefined) {
                            onSchedulePlannedWorkorders([lastSelectedPhaseId])
                            return
                        }
                        onSchedulePlannedWorkorders(allPhaseIds)
                    }}
                >
                    {isMultiPhase
                        ? isAllPhasesView
                            ? 'Schedule All Phases'
                            : 'Schedule Phase'
                        : 'Schedule Planned Workorders'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
